import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { MetricLine } from '@/components/charts/MetricLine'
import { useGpuMetrics, useGpuProcesses } from '@/lib/gpus'
import type { MetricRange } from '@/lib/metrics'
import type { GpuSnapshot } from '@/lib/cluster'
import { bytes, pct, relative } from '@/lib/format'

interface Props {
  nodeId: string
  gpu: GpuSnapshot
  range: MetricRange
}

export function GpuPanel({ nodeId, gpu, range }: Props) {
  const { data, isLoading } = useGpuMetrics(nodeId, gpu.gpu_index, range)
  const { data: procData } = useGpuProcesses(nodeId, gpu.gpu_index, range)

  const points = (data?.points ?? []) as unknown as Record<string, unknown>[]
  const procs = procData?.processes ?? []

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">
          {gpu.gpu_name ?? 'GPU'} #{gpu.gpu_index}
        </CardTitle>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="text-[10px]">
            {gpu.gpu_uuid.slice(0, 12)}…
          </Badge>
          {gpu.temp_c != null && <span>{gpu.temp_c.toFixed(0)}°C</span>}
          {gpu.power_w != null && <span>{gpu.power_w.toFixed(0)}W</span>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && <Skeleton className="h-32" />}
        {!isLoading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Utilization (%)</div>
              <MetricLine
                data={points}
                xKey="b"
                yKey="util_pct"
                format={(v) => pct(v ?? null, 1)}
                label="Util %"
              />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Memory used</div>
              <MetricLine
                data={points}
                xKey="b"
                yKey="mem_used"
                format={(v) => bytes(v ?? null)}
                label="Mem used"
              />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Temperature (°C)</div>
              <MetricLine
                data={points}
                xKey="b"
                yKey="temp_c"
                format={(v) => (v != null ? `${(v as number).toFixed(0)}°C` : '—')}
                label="Temp"
              />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Power (W)</div>
              <MetricLine
                data={points}
                xKey="b"
                yKey="power_w"
                format={(v) => (v != null ? `${(v as number).toFixed(0)}W` : '—')}
                label="Power"
              />
            </div>
          </div>
        )}

        {procs.length > 0 && (
          <div className="border-t pt-3 space-y-1">
            <div className="text-xs text-muted-foreground">Top processes</div>
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left">PID</th>
                  <th className="text-left">Process</th>
                  <th className="text-left">Container</th>
                  <th className="text-right">Avg mem</th>
                  <th className="text-right">Max mem</th>
                  <th className="text-right">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {procs.map((p) => (
                  <tr key={`${p.pid}-${p.container_id ?? 'host'}`} className="border-t">
                    <td>{p.pid}</td>
                    <td>{p.process_name ?? '—'}</td>
                    <td className="font-mono text-[10px]">
                      {p.container_id ? p.container_id.slice(0, 12) : '—'}
                    </td>
                    <td className="text-right">
                      {p.avg_used_mem != null ? bytes(p.avg_used_mem) : '—'}
                    </td>
                    <td className="text-right">
                      {p.max_used_mem != null ? bytes(p.max_used_mem) : '—'}
                    </td>
                    <td className="text-right text-muted-foreground">
                      {relative(p.last_seen)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
